import { Component, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MatDialog, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { extractApiError } from '../../../../core/utils/api-format';
import { QuotaService } from '../../../quotas/services/quota.service';
import { ConclusionVolume } from '../../../quotas/models/conclusion-volume.interface';
import { OepSignatureDialogComponent, OepSignatureDialogData } from '../../../../shared/components/oep-signature-dialog/oep-signature-dialog.component';

export interface EditConclusionVolumeDialogData {
  volume: ConclusionVolume;
}

@Component({
  selector: 'app-edit-conclusion-volume-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatSnackBarModule,
    TranslateModule
  ],
  templateUrl: './edit-conclusion-volume-dialog.component.html',
  styleUrls: ['./edit-conclusion-volume-dialog.component.css']
})
export class EditConclusionVolumeDialogComponent implements OnInit {
  private quotaService = inject(QuotaService);
  private dialog = inject(MatDialog);
  private dialogRef = inject(MatDialogRef<EditConclusionVolumeDialogComponent>);
  private snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  data = inject<EditConclusionVolumeDialogData>(MAT_DIALOG_DATA);

  loading = signal(false);
  errorMessage = signal('');
  meatTypes = signal<any[]>([]);

  applicantName = '';
  quotaYear: number | null = null;
  selectedMeatType: any = null;
  distributedVolume: number | null = null;
  deliveredVolume: number | null = null;
  organizationInn = '';
  personIdnp = '';

  ngOnInit(): void {
    this.prefillBasicFields();
    this.loadMeatTypes();
  }

  prefillBasicFields(): void {
    const volume = this.data.volume;
    this.applicantName = volume.applicantName;
    this.quotaYear = volume.quotaYear;
    // Конвертируем килограммы в тонны для отображения
    this.distributedVolume = volume.distributedVolume / 1000;
    this.deliveredVolume = volume.deliveredVolume / 1000;
  }

  loadMeatTypes(): void {
    this.quotaService.getMeatTypes().subscribe({
      next: (response) => {
        if (response.success && response.res && response.res.content) {
          this.meatTypes.set(response.res.content);
          // Находим и устанавливаем текущий тип мяса из загруженного списка
          const currentMeatTypeId = this.data.volume.meatType.id;
          this.selectedMeatType = response.res.content.find((mt: any) => mt.id === currentMeatTypeId);
        }
      },
      error: (err) => {
        console.error('Error loading meat types:', err);
      }
    });
  }

  isFormValid(): boolean {
    return !!(
      this.applicantName.trim() &&
      this.quotaYear &&
      this.selectedMeatType &&
      this.distributedVolume &&
      this.distributedVolume > 0 &&
      this.deliveredVolume &&
      this.deliveredVolume > 0 &&
      this.organizationInn.trim() &&
      this.personIdnp.trim()
    );
  }

  submit(): void {
    if (!this.isFormValid()) return;

    this.errorMessage.set('');

    // Открываем диалог OEP подписи
    const signatureDialogRef = this.dialog.open(OepSignatureDialogComponent, {
      width: '400px',
      disableClose: true,
      data: {
        organizationInn: this.organizationInn,
        personIdnp: this.personIdnp,
        title: this.translate.instant('editConclusionVolumeDialog.signatureTitle')
      } as OepSignatureDialogData
    });

    signatureDialogRef.afterClosed().subscribe(result => {
      if (result?.success && result.token) {
        this.updateVolume(result.token);
      }
    });
  }

  private updateVolume(token: string): void {
    this.loading.set(true);

    // Конвертируем тонны в килограммы
    const distributedVolumeInKg = this.distributedVolume! * 1000;
    const deliveredVolumeInKg = this.deliveredVolume! * 1000;

    this.quotaService.updateConclusionVolume({
      id: this.data.volume.id,
      applicantName: this.applicantName,
      quotaYear: this.quotaYear!,
      meatType: this.selectedMeatType,
      distributedVolume: distributedVolumeInKg,
      deliveredVolume: deliveredVolumeInKg,
      organizationInn: this.organizationInn,
      personIdnp: this.personIdnp,
      status: this.data.volume.status,
      token: token
    }).subscribe({
      next: (response) => {
        this.loading.set(false);
        if (response.success) {
          this.snackBar.open(this.translate.instant('editConclusionVolumeDialog.success'), 'OK', {
            duration: 3000,
            horizontalPosition: 'center',
            verticalPosition: 'top'
          });
          this.dialogRef.close({ success: true });
        } else {
          const errorMsg = extractApiError(response, this.translate.instant('editConclusionVolumeDialog.error'), this.translate.currentLang);
          this.errorMessage.set(errorMsg);
        }
      },
      error: (error) => {
        console.error('Error updating conclusion volume:', error);
        const errorMsg = extractApiError(error.error, this.translate.instant('editConclusionVolumeDialog.error'), this.translate.currentLang);
        this.errorMessage.set(errorMsg);
        this.loading.set(false);
      }
    });
  }

  close(): void {
    this.dialogRef.close({ success: false });
  }

  getMeatTypeName(meatType: any): string {
    if (!meatType) return '';
    const lang = this.translate.currentLang || 'ru';
    return lang === 'ky' ? meatType.nameKy : meatType.nameRu;
  }
}
